<template>
  <component
    :is="mobile ? 'el-drawer' : 'el-dialog'"
    v-model="dialogVisible"
    class="enterprise-contact-dialog"
    :width="mobile ? undefined : '920px'"
    :size="mobile ? 'min(92vh, 760px)' : undefined"
    :direction="mobile ? 'btt' : undefined"
    append-to-body
    :show-close="!mobile"
    :close-on-click-modal="true"
    :title="$t('layout.layout.enterprise_contact')"
    @open="loadEnterpriseContactConfig"
    @closed="$emit('closed')"
  >
    <div class="enterprise-contact-panel">
      <section class="enterprise-contact-overview">
        <header class="enterprise-contact-intro">
          <h2>{{ $t('layout.layout.enterprise_dialog_heading') }}</h2>
          <p>{{ $t('layout.layout.enterprise_dialog_intro') }}</p>
        </header>

        <div class="enterprise-benefit-grid">
          <article
            v-for="benefit in benefits"
            :key="benefit.titleKey"
            class="enterprise-benefit-item"
          >
            <span class="enterprise-benefit-icon">
              <el-icon><component :is="benefit.icon" /></el-icon>
            </span>
            <div>
              <h3>{{ $t(benefit.titleKey) }}</h3>
              <p>{{ $t(benefit.descriptionKey) }}</p>
            </div>
          </article>
        </div>

        <ul class="enterprise-service-list">
          <li
            v-for="serviceKey in serviceKeys"
            :key="serviceKey"
          >
            {{ $t(serviceKey) }}
          </li>
        </ul>
      </section>

      <aside class="enterprise-contact-advisor">
        <h2>{{ $t('layout.layout.enterprise_advisor_title') }}</h2>
        <p class="enterprise-advisor-description">
          {{ $t('layout.layout.enterprise_advisor_desc') }}
        </p>
        <img
          v-if="showEnterpriseQrCode"
          class="enterprise-qrcode-image"
          :src="enterpriseQrCodeUrl"
          :alt="$t('layout.layout.enterprise_contact')"
          @error="handleEnterpriseQrCodeError"
        >
        <div
          v-else
          class="enterprise-qrcode-placeholder"
          role="img"
          :aria-label="$t('layout.layout.enterprise_qrcode_placeholder')"
        >
          <el-icon><ChatDotRound /></el-icon>
          <span>{{ $t('layout.layout.enterprise_qrcode_placeholder') }}</span>
        </div>
        <p
          v-if="!showEnterpriseQrCode"
          class="enterprise-qrcode-hint"
        >
          {{ $t('layout.layout.enterprise_qrcode_hint') }}
        </p>
      </aside>
    </div>
  </component>
</template>

<script>
import { getPublicClientSysConfig } from '@/api/user'
import {
  ChatDotRound,
  Connection,
  DataAnalysis,
  FolderOpened,
  Medal,
  Service,
  User
} from '@element-plus/icons-vue'

const ENTERPRISE_CONTACT_CONFIG_KEY = 'ENTERPRISE_CONTACT_CONFIG'

function normalizeHttpsUrl(rawUrl) {
  const value = typeof rawUrl === 'string' ? rawUrl.trim() : ''
  if (!value || value.length > 2048 || /\s/.test(value)) return ''

  try {
    const url = new URL(value)
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return ''
    return url.href
  } catch (error) {
    return ''
  }
}

export default {
  name: 'EnterpriseContactDialog',
  components: {
    ChatDotRound
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    mobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'closed'],
  data() {
    return {
      enterpriseContactConfigLoading: false,
      enterpriseQrCodeUrl: '',
      enterpriseQrCodeLoadFailed: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    showEnterpriseQrCode() {
      return Boolean(this.enterpriseQrCodeUrl) && !this.enterpriseQrCodeLoadFailed
    },
    benefits() {
      return [
        {
          icon: Connection,
          titleKey: 'layout.layout.enterprise_benefit_collaboration',
          descriptionKey: 'layout.layout.enterprise_benefit_collaboration_desc'
        },
        {
          icon: Medal,
          titleKey: 'layout.layout.enterprise_benefit_brand',
          descriptionKey: 'layout.layout.enterprise_benefit_brand_desc'
        },
        {
          icon: FolderOpened,
          titleKey: 'layout.layout.enterprise_benefit_assets',
          descriptionKey: 'layout.layout.enterprise_benefit_assets_desc'
        },
        {
          icon: DataAnalysis,
          titleKey: 'layout.layout.enterprise_benefit_flexible',
          descriptionKey: 'layout.layout.enterprise_benefit_flexible_desc'
        },
        {
          icon: User,
          titleKey: 'layout.layout.enterprise_benefit_members',
          descriptionKey: 'layout.layout.enterprise_benefit_members_desc'
        },
        {
          icon: Service,
          titleKey: 'layout.layout.enterprise_benefit_service',
          descriptionKey: 'layout.layout.enterprise_benefit_service_desc'
        }
      ]
    },
    serviceKeys() {
      return [
        'layout.layout.enterprise_service_custom',
        'layout.layout.enterprise_service_scale',
        'layout.layout.enterprise_service_advisor'
      ]
    }
  },
  methods: {
    async loadEnterpriseContactConfig() {
      if (this.enterpriseContactConfigLoading) return

      this.enterpriseContactConfigLoading = true
      try {
        const response = await getPublicClientSysConfig({ key: ENTERPRISE_CONTACT_CONFIG_KEY })
        const responseData = [0, 200].includes(Number(response?.code)) ? response.data : null
        const config = responseData?.value && typeof responseData.value === 'object' && !Array.isArray(responseData.value)
          ? responseData.value
          : responseData

        this.enterpriseQrCodeUrl = normalizeHttpsUrl(config?.qr_code_url)
        this.enterpriseQrCodeLoadFailed = false
      } catch (error) {
        this.enterpriseQrCodeUrl = ''
        this.enterpriseQrCodeLoadFailed = false
        console.warn('加载企微联系配置失败:', error)
      } finally {
        this.enterpriseContactConfigLoading = false
      }
    },
    handleEnterpriseQrCodeError() {
      this.enterpriseQrCodeLoadFailed = true
    }
  }
}
</script>

<style lang="scss">
@use '@/views/mobile/layout/styles/enterprise-contact';
.enterprise-contact-dialog {
  max-width: calc(100vw - 32px);
  padding: 0 !important;
  overflow: hidden;
  border-radius: var(--agst-border-radius-base, 8px);
  background: var(--agst-bg-color, #fff);

  .el-dialog__header {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    width: 48px;
    height: 48px;
    padding: 0;
    margin: 0;
  }

  .el-dialog__title {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .el-dialog__headerbtn {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    border-radius: var(--agst-border-radius-small, 4px);

    &:focus-visible {
      outline: 2px solid var(--agst-color-primary, #2d66f0);
      outline-offset: 2px;
    }
  }

  .el-dialog__body {
    max-height: calc(100dvh - 32px);
    padding: 0;
    overflow-y: auto;
  }

  .enterprise-contact-panel {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(260px, 0.95fr);
    min-height: 520px;
  }

  .enterprise-contact-overview {
    display: flex;
    flex-direction: column;
    padding: 44px 44px 28px;
    background: var(--agst-bg-color, #fff);
  }

  .enterprise-contact-intro {
    h2 {
      margin: 0;
      color: var(--agst-text-color-primary, #303133);
      font-size: 28px;
      font-weight: 700;
      line-height: 1.35;
    }

    p {
      max-width: 520px;
      margin: 10px 0 0;
      color: var(--agst-text-color-secondary, #909399);
      font-size: var(--agst-font-size-base, 14px);
      line-height: 1.65;
    }
  }

  .enterprise-benefit-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px 36px;
    margin-top: 34px;
  }

  .enterprise-benefit-item {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr);
    gap: 14px;
    align-items: start;

    h3 {
      margin: 1px 0 5px;
      color: var(--agst-text-color-primary, #303133);
      font-size: var(--agst-font-size-medium, 16px);
      font-weight: 600;
      line-height: 1.4;
    }

    p {
      margin: 0;
      color: var(--agst-text-color-secondary, #909399);
      font-size: var(--agst-font-size-small, 13px);
      line-height: 1.55;
    }
  }

  .enterprise-benefit-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: var(--agst-color-primary, #2d66f0);
    background: var(--agst-color-primary-light-9, #eaf0fe);

    .el-icon {
      font-size: 20px;
    }
  }

  .enterprise-service-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    padding: 22px 0 0;
    margin: auto 0 0;
    border-top: 1px solid var(--agst-border-color-lighter, #ebeef5);
    list-style: none;

    li {
      position: relative;
      padding-left: 18px;
      color: var(--agst-text-color-regular, #606266);
      font-size: var(--agst-font-size-small, 13px);
      line-height: 1.5;

      &::before {
        content: '';
        position: absolute;
        top: 7px;
        left: 0;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--agst-color-primary, #2d66f0);
      }
    }
  }

  .enterprise-contact-advisor {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 68px 32px 32px;
    text-align: center;
    background: var(--agst-fill-color-light, #f5f7fa);

    h2 {
      margin: 0;
      color: var(--agst-text-color-primary, #303133);
      font-size: 24px;
      font-weight: 700;
      line-height: 1.4;
    }
  }

  .enterprise-advisor-description {
    margin: 14px 0 24px;
    color: var(--agst-text-color-secondary, #909399);
    font-size: var(--agst-font-size-base, 14px);
    line-height: 1.65;
    text-align: left;
  }

  .enterprise-qrcode-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 190px;
    height: 190px;
    box-sizing: border-box;
    border: 1px dashed var(--agst-border-color, #dcdfe6);
    border-radius: var(--agst-border-radius-small, 4px);
    color: var(--agst-text-color-secondary, #909399);
    background: #fff;

    .el-icon {
      color: var(--agst-color-primary, #2d66f0);
      font-size: 42px;
    }

    span {
      font-size: var(--agst-font-size-small, 13px);
    }
  }

  .enterprise-qrcode-image {
    display: block;
    width: 190px;
    height: 190px;
    box-sizing: border-box;
    border: 1px solid var(--agst-border-color, #dcdfe6);
    border-radius: var(--agst-border-radius-small, 4px);
    object-fit: contain;
    background: #fff;
  }

  .enterprise-qrcode-hint {
    margin: 16px 0 0;
    color: var(--agst-text-color-regular, #606266);
    font-size: var(--agst-font-size-small, 13px);
    line-height: 1.5;
  }
}

.enterprise-contact-dialog.el-drawer {
  box-sizing: border-box;
  width: 100% !important;
  max-width: 960px;
  max-height: min(92vh, 760px);
  max-height: min(92dvh, 760px);
  padding: 0;
  border-radius: 16px 16px 0 0;

  .el-drawer__header { flex: 0 0 auto; min-height: 0; padding: 0; margin: 0; }
  .el-drawer__title { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
  .el-drawer__close-btn { z-index: 2; top: 8px; right: 8px; width: 44px; height: 44px; }
  .el-drawer__body { min-height: 0; padding: 0; overflow-y: auto; }
}

html[data-device='ipad'][data-layout-mode='mobile'] {
  .enterprise-contact-dialog .el-dialog__headerbtn {
    top: 6px;
    right: 6px;
    width: 44px;
    height: 44px;
  }
}

@media (max-width: 768px) {
  .enterprise-contact-dialog {
    max-width: calc(100vw - 24px);

    .el-dialog__headerbtn {
      top: 6px;
      right: 6px;
      width: 44px;
      height: 44px;
    }

    .el-dialog__body {
      max-height: calc(100dvh - 24px);
    }

    .enterprise-contact-panel {
      grid-template-columns: minmax(0, 1fr);
      min-height: 0;
    }

    .enterprise-contact-overview {
      padding: 36px 24px 28px;
    }

    .enterprise-contact-intro h2 {
      padding-right: 28px;
      font-size: 24px;
    }

    .enterprise-benefit-grid {
      gap: 24px;
      margin-top: 28px;
    }

    .enterprise-service-list {
      margin-top: 30px;
    }

    .enterprise-contact-advisor {
      padding: 34px 24px 30px;
    }

    .enterprise-advisor-description {
      max-width: 420px;
      text-align: center;
    }
  }

  .enterprise-contact-dialog.el-drawer {
    max-width: none;

    .el-drawer__close-btn { top: 6px; right: 6px; width: 44px; height: 44px; }

    .enterprise-contact-panel {
      grid-template-columns: minmax(0, 1fr);
      min-height: 0;
    }

    .enterprise-contact-overview { padding: 36px 24px 28px; }
    .enterprise-contact-intro h2 { padding-right: 28px; font-size: 24px; }
    .enterprise-benefit-grid { gap: 24px; margin-top: 28px; }
    .enterprise-service-list { margin-top: 30px; }
    .enterprise-contact-advisor { padding: 34px 24px 30px; }
    .enterprise-advisor-description { max-width: 420px; text-align: center; }
  }
}

@media (max-width: 560px) {
  .enterprise-contact-dialog {
    .enterprise-benefit-grid,
    .enterprise-service-list {
      grid-template-columns: minmax(0, 1fr);
    }

    .enterprise-service-list {
      gap: 10px;
    }
  }
}
</style>
